import type {HtmlCssStructureApiRequest} from '@features/api/chat/schema';
import type {
  HtmlCssPostprocessApiRequest,
  HtmlCssPostprocessApiResponse,
} from '@/pages/api/htmlCssGen/postprocess';
import {parseJsonEventStream} from 'ai';
import {
  type AgentUIMessageFullChunk,
  type ClarifyingQuestion,
} from '../prompt/agent/tools/common/messages';
import z from 'zod';
import {isDataChunk, StructureState} from './StructureState';
import {isModernGenType} from '@features/server/utils/getEffectiveGenType';
import type {GenType} from '@features/flow/flowService';
import {isStubHtmlResponse} from '../util/isStubHtmlResponse';
import type {SessionLimitInfo} from '@features/subscription/sessionLimitAtom';

/**
 * Agentic edits stream one chunk per tool-call token. Recomputing the full
 * document (DOMParser in computeHtml) and re-rendering the frame on every
 * chunk saturates the main thread and freezes the tab (multiple concurrent
 * variation streams make it fatal). Coalesce renders to one per interval.
 */
const RENDER_FLUSH_MS = 100;

const sessionLimitResponseSchema = z.object({
  error: z.literal('session-limit'),
  limitedBy: z.enum(['session', 'day']),
  resetsAt: z.string(),
});

export async function streamAgent({
  flowId,
  screenId,
  signal,
  abortToken,
  setState,
  updateScreen,
  invalidateScreen,
  getScreenContents,
  onClarifyingQuestions,
  onClarifyingQuestionsDone,
  onSessionLimit,
  setGentype,
  pushUpdate,
}: GenerateStructureProps) {
  setState('connecting');
  const structureResponse = await fetch('/api/htmlCssGen/agent', {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    signal,
    body: JSON.stringify({
      flowId,
      screenId,
      abortToken,
    } satisfies HtmlCssStructureApiRequest),
  });
  signal?.throwIfAborted();

  if (structureResponse.status === 204) {
    console.log('nothing to do on screen', screenId);
    setState('idle');
    return;
  }

  if (structureResponse.status === 429) {
    // .catch: an infra-level 429 (proxy/platform) has a non-JSON body.
    const limit = sessionLimitResponseSchema.safeParse(
      await structureResponse.json().catch(() => null),
    );
    if (limit.success) {
      onSessionLimit?.({
        limitedBy: limit.data.limitedBy,
        resetsAt: new Date(limit.data.resetsAt),
      });
    }
    setState('idle');
    return;
  }

  if (!structureResponse.ok) {
    const errorText = await structureResponse.text();
    throw new Error(`HTML/CSS structure generation failed: ${errorText}`);
  }
  if (!structureResponse.body) throw new Error('No response body');

  pushUpdate('Thinking…');
  setState('generating');
  const chunkStream = parseJsonEventStream<AgentUIMessageFullChunk>({
    stream: structureResponse.body,
    schema: z.any(),
  });

  const reader = chunkStream.getReader();

  // Cancel the reader when abort fires to prevent unhandled rejections
  // from the internal parseJsonEventStream TransformStream piping.
  const abortHandler = () => {
    reader.cancel().catch(() => {});
  };
  signal?.addEventListener('abort', abortHandler, {once: true});

  const structureMap = new Map<string, StructureState>();
  const touchedScreenIds = new Set<string>();

  const flushTimers = new Map<string, ReturnType<typeof setTimeout>>();
  const flushScreen = (structure: StructureState) => {
    updateScreen(
      structure.screenId,
      structure.computeHtml(),
      structure.newName,
    );
  };
  const scheduleFlush = (structure: StructureState) => {
    if (flushTimers.has(structure.screenId)) return;
    flushTimers.set(
      structure.screenId,
      setTimeout(() => {
        // eslint-disable-next-line drizzle/enforce-delete-with-where
        flushTimers.delete(structure.screenId);
        try {
          flushScreen(structure);
        } catch {
          // computeHtml can throw (e.g. invalid selector from the model).
          // Swallow here — the final flush rethrows it on the generation
          // promise, where it's handled. An uncaught timer error would not be.
        }
      }, RENDER_FLUSH_MS),
    );
  };

  try {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    while (true) {
      const {done, value: chunk} = await reader.read();
      if (done) break;

      signal?.throwIfAborted();

      if (!chunk.success) throw chunk.error;

      // Handle clarifying questions separately from structure state
      if (
        chunk.value.type === 'data-askClarifyingQuestions' &&
        'data' in chunk.value
      ) {
        const data = chunk.value.data;
        setState('asking-questions');
        onClarifyingQuestions?.(data.questions);
        continue;
      }
      if (chunk.value.type === 'data-clarifyingQuestionsAnswered') {
        setState('generating');
        onClarifyingQuestionsDone?.();
        continue;
      }
      if (chunk.value.type === 'data-setGenType') {
        setGentype(chunk.value.data.screenId, chunk.value.data.genType);
      }
      if (chunk.value.type === 'data-pushStatusUpdate') {
        pushUpdate(chunk.value.data.update, chunk.value.data.details);
      }

      const structure = await getStructureState(
        chunk.value,
        structureMap,
        screenId,
        getScreenContents,
      );
      if (structure) {
        structure.handleMessage(chunk.value);
        if (
          chunk.value.type === 'data-clearScreen' ||
          chunk.value.type === 'data-appendHtml'
        ) {
          touchedScreenIds.add(structure.screenId);
        }
        scheduleFlush(structure);
      }
    }

    for (const structure of structureMap.values()) {
      flushScreen(structure);
    }
  } finally {
    for (const timer of flushTimers.values()) {
      clearTimeout(timer);
    }
    flushTimers.clear();
    signal?.removeEventListener('abort', abortHandler);
  }

  signal?.throwIfAborted();

  setState('postprocessing');
  // Normalize legacy genTypes for postprocess

  const stubScreenIds = new Set<string>();

  for (const [screenId, structureState] of structureMap.entries()) {
    const computedHtml = structureState.computeHtml();

    // Skip postprocess for screens with no HTML or stub HTML (e.g. "...")
    if (isStubHtmlResponse(computedHtml)) {
      stubScreenIds.add(screenId);
      continue;
    }

    const streamGenType = structureState.genType;
    const normalizedGenType =
      streamGenType === 'mobile'
        ? 'mobile-html-css'
        : streamGenType === 'web'
          ? 'web-html-css'
          : isModernGenType(streamGenType)
            ? streamGenType
            : undefined;

    const postprocessBody = {
      flowId,
      chatId: screenId,
      structureResponse: JSON.stringify({
        screenName: structureState.newName,
        html: computedHtml,
      }),
      // Pass genType from stream (pending message is deleted before postprocess runs)
      genType: normalizedGenType,
    } satisfies HtmlCssPostprocessApiRequest;

    const postprocessResponse = await fetch('/api/htmlCssGen/postprocess', {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(postprocessBody),
    });

    if (!postprocessResponse.ok) {
      const errorText = await postprocessResponse.text();
      throw new Error(`HTML/CSS postprocessing failed: ${errorText}`);
    }
    const postprocessData =
      (await postprocessResponse.json()) as HtmlCssPostprocessApiResponse;
    updateScreen(screenId, postprocessData.finalStructure, null);
    await invalidateScreen(flowId, screenId, postprocessData.botMessageId);
  }

  setState('idle');
  updateScreen(screenId, null, null);

  // Exclude stubs — they never persisted as real output.
  for (const id of stubScreenIds) {
    // eslint-disable-next-line drizzle/enforce-delete-with-where
    touchedScreenIds.delete(id);
  }
  return {stubScreenIds, touchedScreenIds};
}

export type GenerationStatus =
  | 'idle'
  | 'connecting'
  | 'generating'
  | 'asking-questions'
  | 'postprocessing'
  | 'invalidating';

interface GenerateStructureProps {
  flowId: string;
  screenId: string;
  signal?: AbortSignal;
  abortToken: string;

  /**
   * Invalidates the screen and blocks until the active structure is refetched.
   * By doing this, we avoid the "end-of-generation-flicker" thingy.
   */
  invalidateScreen: (
    flowId: string,
    screenId: string,
    messageId: string,
  ) => Promise<void>;

  /** for agentic usecases */
  getScreenContents: (screenId: string) => Promise<string>;

  /** needs screenId for agentic usecases */
  updateScreen: (
    screenId: string,
    html: string | null,
    name: string | null,
  ) => void;
  setState: (state: GenerationStatus) => void;

  /** Called when agent asks clarifying questions */
  onClarifyingQuestions?: (questions: ClarifyingQuestion[]) => void;
  /** Called when clarifying questions flow completes */
  onClarifyingQuestionsDone?: () => void;
  /** Called when the server blocks the request with a session limit */
  onSessionLimit?: (
    info: Omit<SessionLimitInfo, 'flowId' | 'screenId'>,
  ) => void;
  setGentype: (screenId: string, state: GenType) => void;

  pushUpdate: (update: string, details?: string) => void;
}

async function getStructureState(
  chunk: AgentUIMessageFullChunk,
  structureMap: Map<string, StructureState>,
  screenId: string,
  getScreenContents: (screenId: string) => Promise<string>,
) {
  if (!isDataChunk(chunk)) {
    return null;
  }

  if ('screenId' in chunk.data) {
    const structure = structureMap.get(chunk.data.screenId);
    if (structure) {
      return structure;
    }

    const html =
      chunk.type === 'data-clearScreen'
        ? ''
        : await getScreenContents(chunk.data.screenId);
    const newStruct = new StructureState(chunk.data.screenId, html);
    structureMap.set(chunk.data.screenId, newStruct);
    return newStruct;
  }

  const scrStruct = structureMap.get(screenId);
  if (scrStruct) {
    return scrStruct;
  }

  const newScreenStructure = new StructureState(
    screenId,
    await getScreenContents(screenId),
  );
  structureMap.set(screenId, newScreenStructure);

  return newScreenStructure;
}
